import styles from "../EventSubmissionForm/EventSubmissionForm.module.scss";
import i18n from "../../i18n";
import PropTypes from "prop-types";
import React, {useState} from "react";
import * as Yup from "yup";
import {Form, Formik} from "formik";
import {RadioDateInputType} from "../EventSubmissionForm/EventSubmissionForm";
import inputGroupStyles from "../InputGroup/InputGroup.module.scss";
import InputGroup from "../InputGroup";
import buttonStyles from "../Button/Button.module.scss";
import Button from "../Button";
import {InputGroupV2, PrevAndSubmitButtons} from "./EventSubmissionFormV2";
import Icon from "../Icon";

const uuidv4 = () => {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(
        /[xy]/g,
        function (c) {
            var r = (Math.random() * 16) | 0,
                v = c == 'x' ? r : (r & 0x3) | 0x8;
            return v.toString(16);
        }
    );
};

const singleDateObject = (date, startTime, endTime) => ({
    id: uuidv4(),
    type: 'single_date',
    value: {
        date,
        startTime,
        endTime,
    },
});
const dateRangeObject = (startDate, endDate) => ({
    id: uuidv4(),
    type: 'date_range',
    value: {startDate, endDate},
});

export const getFormattedDate = (inputDate, config = {showDayName: true}) => {
    const lang = window.location.hostname.includes("visitstockholm.com")
        ? 'en'
        : 'sv'
    const dayNames = lang === 'en'
        ? ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
        : ['Sön', 'Mån', 'Tis', 'Ons', 'Tor', 'Fre', 'Lör'];
    const monthNames = lang === 'en'
        ? ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
        : ['Jan', 'Feb', 'Mar', 'Apr', 'Maj', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dec'];

    // inputDate = YYYY-MM-DD
    const date = new Date(inputDate);
    const dayName = dayNames[date.getDay()];
    const day = date.getDate();
    const monthName = monthNames[date.getMonth()];

    return config.showDayName ? `${dayName} ${day} ${monthName}` : `${day} ${monthName}`
}

const DateAdminValueList = ({values, onRemove, setFieldValue}) => {
    return (
        <>
            <h3 className={styles['EventSubmissionForm__DateValuesHeading']}>
                {i18n.t(
                    'EventSubmissionFormV2.dateValuesHeading'
                )}
            </h3>
            <ul className={styles['EventSubmissionForm__DateValues']}>
                {values.dateAdmin.map(({value, id}, i) => {
                    return (
                        <li
                            key={i}
                            className={styles['EventSubmissionForm__DateValue']}
                        >
                            {!!value.startDate && (
                                <span
                                    className={
                                        styles['EventSubmissionForm__DateValueText']
                                    }
                                >
                                    {getFormattedDate(value.startDate)} - {getFormattedDate(value.endDate)}
                                </span>
                            )}
                            {!!value.date && (
                                <span>
                                    {getFormattedDate(value.date)}, {value.startTime} — {value.endTime}
                        <       /span>
                            )}
                            <button
                                type="button"
                                className={
                                    styles['EventSubmissionForm__DateValueRemove']
                                }
                                aria-label={i18n.t(
                                    'EventSubmissionForm.removeDateValue'
                                )}
                                onClick={(e) => {
                                    onRemove(id, values, setFieldValue);
                                    e.preventDefault();
                                }}
                            >
                                <Icon type={"close"} color={"pink"}/>
                            </button>
                        </li>
                    );
                })}
            </ul>
        </>
    )
};

DateAdminValueList.propTypes = {
    values: PropTypes.object,
    onRemove: PropTypes.func,
    setFieldValue: PropTypes.func,
};

const isTime = (value) => /^([0-1][0-9]|[2][0-3]):([0-5][0-9])$/gm.test(value);

const DateStep = ({
                       initialValues,
                       validationSchema,
                       onClickPrev,
                       onSubmit,
                   }) => {
    const [showSingleDate, setShowSingleDate] = useState(true);
    const [dateValidationError, setDateValidationError] = useState({});

    const handleAddDateRange = (values, setFieldValue) => {
        const {dateAdmin, startDate, endDate} = values;
        const schema = Yup.object().shape({
            startDate: Yup.date().typeError(
                i18n.t('EventSubmissionForm.validation.date')
            ),
            endDate: Yup.date()
                .typeError(i18n.t('EventSubmissionForm.validation.date'))
                .min(Yup.ref('startDate')),
        });

        schema
            .validate({
                endDate,
                startDate,
            })
            .then(function () {
                setFieldValue('dateAdmin', [
                    ...dateAdmin,
                    dateRangeObject(startDate, endDate),
                ]);
                // TODO: Fix: This hook doesn't exist but somehow makes the form validation to rerender correctly.
                setDates({
                    ...values, dateAdmin: [
                        ...dateAdmin,
                        dateRangeObject(startDate, endDate),
                    ]
                })
                setFieldValue('startDate', '');
                setFieldValue('endDate', '');
                setDateValidationError({});
            })
            .catch(function (err) {
                const {path, message} = err;
                setDateValidationError({
                    [path]: message,
                });
            });
    };

    const handleAddSingleDate = (values, setFieldValue) => {
        const {dateAdmin, date, startTime, endTime} = values;
        let schema = Yup.object().shape({
            date: Yup.date().typeError(
                i18n.t('EventSubmissionForm.validation.date')
            ),
            startTime: Yup.string()
                .required(i18n.t('EventSubmissionForm.validation.required'))
                .test(
                    'is-time-format',
                    i18n.t('EventSubmissionForm.validation.time'),
                    isTime
                ),
            endTime: Yup.string()
                .required(i18n.t('EventSubmissionForm.validation.required'))
                .test(
                    'is-time-format',
                    i18n.t('EventSubmissionForm.validation.time'),
                    isTime
                ),
        });

        schema
            .validate({
                date,
                endTime,
                startTime,
            })
            .then(function () {
                setFieldValue('dateAdmin', [
                    ...dateAdmin,
                    singleDateObject(date, startTime, endTime),
                ]);
                // TODO: Fix: This hook doesn't exist but somehow makes the form validation to rerender correctly.
                setDates({
                    ...values, dateAdmin: [
                        ...dateAdmin,
                        singleDateObject(date, startTime, endTime),
                    ]
                })
                setFieldValue('date', '');
                setFieldValue('startTime', '');
                setFieldValue('endTime', '');
                setDateValidationError({});
            })
            .catch(function (err) {
                const {path, message} = err;
                setDateValidationError({
                    [path]: message,
                });
            });
    };

    const handleRemoveDateAdminItem = (id, values, setFieldValue) => {
        const {dateAdmin} = values;
        setFieldValue(
            'dateAdmin',
            dateAdmin.filter((item) => item.id !== id)
        );
    };

    return (
        <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={onSubmit}
        >{({values, setFieldValue}) => (
            <Form className={styles['EventSubmissionForm--V2']}>
                <legend
                    className={
                        styles[
                            'EventSubmissionForm__SectionHeader'
                            ] +
                        ' ' +
                        styles[
                            'EventSubmissionForm__SectionHeader--Required'
                            ]
                    }
                >
                    {i18n.t(
                        'EventSubmissionFormV2.sectionHeaderDates'
                    )}
                </legend>
                <RadioDateInputType
                    onChange={setShowSingleDate}
                />

                {!!showSingleDate && (
                    <>
                        <div
                            className={
                                styles[
                                    'EventSubmissionForm__DateContainer'
                                    ] +
                                " " +
                                styles[
                                    'EventSubmissionForm__DateContainer--SingleDate'
                                    ]
                            }
                        >
                            <InputGroupV2
                                placeholder="Date"
                                type="date"
                                name="date"
                                id="date"
                                label={i18n.t(
                                    'EventSubmissionForm.labels.date'
                                )}
                                modifiers={[
                                    inputGroupStyles[
                                        'InputGroup--Inline'
                                        ],
                                ]}
                                extraErrorMessage={
                                    dateValidationError?.date
                                }
                            />
                            <InputGroupV2
                                type="time"
                                name="startTime"
                                id="startTime"
                                placeholder="Start time"
                                label={i18n.t(
                                    'EventSubmissionForm.labels.startTime'
                                )}
                                modifiers={[
                                    inputGroupStyles[
                                        'InputGroup--Inline'
                                        ],
                                ]}
                                extraErrorMessage={
                                    dateValidationError?.startTime
                                }
                            />
                            <InputGroupV2
                                type="time"
                                name="endTime"
                                id="endTime"
                                placeholder="End time"
                                label={i18n.t(
                                    'EventSubmissionForm.labels.endTime'
                                )}
                                modifiers={[
                                    inputGroupStyles[
                                        'InputGroup--Inline'
                                        ],
                                ]}
                                extraErrorMessage={
                                    dateValidationError?.endTime
                                }
                            />
                        </div>
                        <Button
                            type="secondaryInvert"
                            text={
                                i18n.t(
                                    'EventSubmissionFormV2.buttons.addDate'
                                )
                            }
                            onClick={(e) => {
                                handleAddSingleDate(
                                    values,
                                    setFieldValue,
                                );
                                e.preventDefault();
                            }}
                            modifiers={[buttonStyles['Button--PreventNoIconStyle']]}
                        />
                    </>
                )}

                {!showSingleDate && (
                    <>
                        <div
                            className={
                                styles[
                                    'EventSubmissionForm__DateContainer'
                                    ] + " " +
                                styles[
                                    'EventSubmissionForm__DateContainer--DateRange'
                                    ]
                            }
                        >
                            <InputGroupV2
                                placeholder="Start"
                                type="date"
                                name="startDate"
                                id="startDate"
                                label={i18n.t(
                                    'EventSubmissionForm.labels.startDate'
                                )}
                                modifiers={[
                                    inputGroupStyles[
                                        'InputGroup--Inline'
                                        ],
                                ]}
                                extraErrorMessage={
                                    dateValidationError?.startDate
                                }
                            />

                            <InputGroupV2
                                type="date"
                                name="endDate"
                                id="endDate"
                                placeholder="End"
                                label={i18n.t(
                                    'EventSubmissionForm.labels.endDate'
                                )}
                                modifiers={[
                                    inputGroupStyles[
                                        'InputGroup--Inline'
                                        ],
                                ]}
                                extraErrorMessage={
                                    dateValidationError?.endDate
                                }
                            />
                        </div>
                        <Button
                            type="secondaryInvert"
                            text={
                                i18n.t(
                                    'EventSubmissionFormV2.buttons.addDate'
                                )}
                            onClick={(e) => {
                                handleAddDateRange(
                                    values,
                                    setFieldValue,
                                    // validateForm,
                                );
                                e.preventDefault();
                            }}
                            modifiers={[buttonStyles['Button--PreventNoIconStyle']]}
                        />
                    </>
                )}

                <InputGroup name="dateAdmin" type="hidden"/>
                {!!values.dateAdmin?.length && (
                    <DateAdminValueList
                        onRemove={handleRemoveDateAdminItem}
                        setFieldValue={setFieldValue}
                        values={values}
                    />
                )}
                <PrevAndSubmitButtons onClickPrev={onClickPrev}/>
            </Form>
        )}
        </Formik>
    )
}

export default DateStep;
